import { Formik } from 'formik'
import * as React from 'react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Keyboard } from 'react-native'
import * as Yup from 'yup'
import { Box } from '../Box'
import { Button } from '../Button'
import { Text } from '../Text'
import { FormGenerator } from './FormGenerator'
import { styles } from './styles'
import type { ActionButtonProps, FormProps, FormTypes, RefCalback, RenderFormProps } from './types'

interface AddMoreData {
  [key: string]: number
}

const createSchemaFromItem = (formItem, values) => {
  return Yup.object().shape(
    formItem.formItems
      .filter((item) => {
        if (item.hideComponent && item.hideComponent(values)) {
          if (values[item.fieldName]) {
            values[item.fieldName] = undefined
          }
          return false
        }
        if (item?.dependency) {
          let flag = true
          for (let i = 0; i < item.dependency.length - 1; i += 2) {
            if (values[item.dependency[i]] !== item.dependency[i + 1]) {
              flag = false
              break
            }
          }
          return flag && item
        } else {
          return item
        }
      })
      .reduce((accItem, curItem) => {
        if (curItem.validationSchema) {
          return {
            ...accItem,
            [curItem.fieldName]: curItem.validationSchema,
          }
        } else {
          const horizonntalSchema = horSchema(curItem?.horizontalComps, values)
          return { ...accItem, ...horizonntalSchema }
        }
        // return accItem
      }, {}),
  )
}

const horSchema: any = (data: any, values) => {
  let retData = {}
  data
    ?.filter((horItem: any) => {
      if (horItem?.dependency) {
        let flag = true
        for (let i = 0; i < horItem.dependency.length - 1; i += 2) {
          if (
            typeof horItem.dependency[i + 1] === 'string' &&
            values[horItem.dependency[i]] !== horItem.dependency[i + 1]
          ) {
            flag = false
            break
          }
          if (typeof horItem.dependency[i + 1] !== 'string') {
            for (let j = 0; j < horItem.dependency[i + 1].length; j++) {
              if (values[horItem.dependency[i]] === horItem.dependency[i + 1][j]) {
                flag = true
                break
              } else {
                flag = false
              }
            }
          }
        }
        return flag && horItem
      } else if (horItem?.hideComponent?.(values)) {
        if (values[horItem.fieldName]) {
          values[horItem.fieldName] = ''
        }
        return false
      } else {
        return horItem
      }
    })
    .map((curHorItem: any) => {
      if (curHorItem.validationSchema) {
        retData = {
          ...retData,
          [curHorItem.fieldName]: curHorItem.validationSchema,
        }
      }
      return retData
    })
  return retData
}

const RenderForm = forwardRef(
  (
    {
      container: Container,
      formData,
      onFormSubmit,
      actionButtons,
      setTitle,
      isLoading,
      initialStep,
      setError,
      onAddMoreClicked,
      allTouched,
      ...propsFromFormik
    }: RenderFormProps,
    ref: React.Ref<RefCalback>,
  ) => {
    const initStep = formData.findIndex((item) => item.key === initialStep)
    const [, setReload] = useState(false)
    const [validForm, setValidForm] = useState(false)
    const reloadStep = () => {
      setReload((currReload) => !currReload)
    }
    const totalSteps = formData.length
    const backStack = useRef<Array<number>>([])
    const stepValidationStatus = useRef<Array<boolean>>([])
    const [step, setStep] = useState(initStep === -1 ? 0 : initStep)
    const [addmoreCount, setAddMoreCount] = useState({} as AddMoreData)

    const addMoreClicked = () => {
      if (formData[step].addMore) {
        const currentCount = (addmoreCount[step.toString()] ?? 0) + 1
        const alreadyExist = formData[step].formItems.some(
          (item) => item?.fieldName === `${formData[step].addMore![0]}_${currentCount}`,
        )
        if (!alreadyExist) {
          const horAddMoreComps: any = []
          const newValues = formData[step].addMore!.map((item) => {
            const currentOriginal = formData[step].formItems.find((formItem) => formItem?.fieldName === item)
            const currentHorizontalOriginal = formData[step].formItems.find((formItem) => {
              if (formItem?.horizontalComps) {
                return formItem?.horizontalComps?.find((horItem) => horItem?.fieldName === item)
              }
            })
            if (!currentOriginal && !currentHorizontalOriginal) {
              throw `Addmore property ${item} does not exist`
            }
            if (currentOriginal) {
              let updatedDependency = []
              if (currentOriginal.dependency) {
                const cloneDependency: any = JSON.parse(JSON.stringify(currentOriginal?.dependency))
                updatedDependency = cloneDependency.map((dependency: any, index: number) =>
                  index % 2 === 0 ? `${dependency}_${currentCount}` : dependency,
                )
              }
              let copyObject = Object.assign({}, currentOriginal, {
                fieldName: `${item}_${currentCount}`,
                currentCount: currentCount,
              })
              copyObject =
                updatedDependency && updatedDependency.length > 0
                  ? Object.assign({}, copyObject, { dependency: updatedDependency })
                  : copyObject
              return copyObject
              // return {
              //   ...currentOriginal,
              //   fieldName: `${item}_${currentCount}`,
              //   currentCount: currentCount,
              //   //dependency: updatedDependency,
              // }
            }
            const length = currentHorizontalOriginal?.horizontalComps?.length ?? -1
            if (currentHorizontalOriginal) {
              currentHorizontalOriginal?.horizontalComps?.map((horItem) => {
                if (horItem?.fieldName === item) {
                  horAddMoreComps.push({
                    ...horItem,
                    fieldName: `${item}_${currentCount}`,
                  })
                }
              })
            }
            if (horAddMoreComps?.length === length) {
              return { horizontalComps: horAddMoreComps }
            }
          })
          //console.log('newValues', newValues)
          formData[step].formItems.push(...newValues)
          setAddMoreCount({ ...addmoreCount, [step.toString()]: currentCount })
          if (onAddMoreClicked) {
            onAddMoreClicked({ formData, step, currentCount })
          }
        }
      }
    }
    const deleteRowClicked = () => {
      if (formData[step].addMore) {
        const currentCount = addmoreCount[step.toString()] ?? 0

        const addedRowExist = formData[step].formItems.some(
          (item) => item.fieldName === `${formData[step].addMore![0]}_${currentCount}`,
        )
        if (addedRowExist) {
          formData[step].addMore!.map(() => {
            formData[step].formItems.pop()
          })
          setAddMoreCount({
            ...addmoreCount,
            [step.toString()]: currentCount - 1,
          })
        }
      }
    }
    const schemaArray = formData.map((stepData) => createSchemaFromItem(stepData, propsFromFormik.values))
    const formSchema = schemaArray[step]

    useEffect(() => {
      if (setTitle && formData[step].title) {
        setTitle(formData[step].title!)
      }
    }, [formData, setTitle, step])

    useEffect(() => {
      if (initialStep) {
        setStep(initStep)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initStep])

    useImperativeHandle(ref, () => ({
      goBack: () => {
        let lastStep = backStack.current.pop()
        if (lastStep !== undefined && formData[lastStep].backNotAllowed) {
          backStack.current.push(lastStep)
          return false
        }
        if (lastStep === step) {
          lastStep = backStack.current.pop()
        }
        if (lastStep !== undefined) {
          setStep(lastStep)
          return true
        }
        return false
      },
      moveToNextStep: () => {
        if (!formData[step].noBackStack && !backStack.current.includes(step)) {
          backStack.current.push(step)
        }
        setStep(step + 1)
      },
      getValues: () => {
        return propsFromFormik.values
      },
      setFieldValue: (key, value) => {
        propsFromFormik.setFieldValue(key, value)
      },
    }))
    const defaultActionButtonOnSubmit = async () => {
      let orgItems: any
      try {
        formData[step].formItems
          .filter((item) => item?.hide !== true)
          .forEach((item) => {
            item?.fieldName && propsFromFormik.setFieldTouched(item?.fieldName, true)
            if (item.horizontalComps) {
              item.horizontalComps
                ?.filter((horItem) => horItem?.hide !== true)
                ?.forEach((element) => {
                  element?.fieldName && propsFromFormik.setFieldTouched(element?.fieldName, true)
                })
            }
          })
        Keyboard.dismiss()
        const isValid = await formSchema?.isValid(propsFromFormik.values)
        if (isValid) {
          let nextStep = step + 1
          if (formData[step].onSubmit) {
            const items = formData[step].formItems.filter((item) => item?.isOnLoadEditable === false)
            orgItems = JSON.parse(JSON.stringify(items))
            items.forEach((item) => {
              item.editable = false
            })
            reloadStep()
            if (isLoading) {
              isLoading(true)
            }
            const key = await formData[step].onSubmit!(propsFromFormik.values, formData, reloadStep)
            if (isLoading) {
              isLoading(false)
            }
            items.forEach((item) => {
              const lItem = orgItems.find((orgItem: any) => item.fieldName === orgItem.fieldName)
              item.editable =
                item.isEditable === undefined ? (lItem.editable === undefined ? true : lItem.editable) : item.isEditable
            })
            reloadStep()
            if (key === 'end') {
              return false
            }
            if (key) {
              nextStep = formData.findIndex((item) => item.key === key)
            }
          }
          if (nextStep < totalSteps && nextStep >= 0) {
            if (!formData[step].noBackStack && !backStack.current.includes(step)) {
              backStack.current.push(step)
            }
            setStep(nextStep)
          }
        }
      } catch (ex) {
        if (setError) {
          setError(ex)
        }
        if (isLoading) {
          isLoading(false)
        }
        let items = formData[step].formItems.filter((item) => item?.isOnLoadEditable === false)
        items.forEach((item) => {
          const lItem = orgItems.find((orgItem: any) => item.fieldName === orgItem.fieldName)
          item.editable =
            item.isEditable === undefined ? (lItem.editable === undefined ? true : lItem.editable) : item.isEditable
        })
        reloadStep()
      }
      return true
    }
    useEffect(() => {
      if (formData[step].validateForm) {
        const fallBackkey = formData[step].validateForm!(propsFromFormik.values)
        if (fallBackkey) {
          const nextStep = formData.findIndex((item) => item.key === fallBackkey)
          if (nextStep >= 0 && nextStep < totalSteps) {
            setStep(nextStep)
          }
        }
      }
    }, [step, propsFromFormik.values, totalSteps, formData])

    const getFilteredItems = () => {
      const filteredElemnts: any = []
      formData[step].formItems.filter((item) => {
        if (item?.dependency || item?.hideComponent) {
          let flag = true
          if (item?.dependency) {
            for (let i = 0; i < item.dependency.length - 1; i += 2) {
              if (propsFromFormik.values[item.dependency[i]] !== item.dependency[i + 1]) {
                flag = false
                break
              }
            }
          }
          if (flag && item?.hideComponent) {
            flag = !item?.hideComponent(propsFromFormik.values)
          }
          flag && filteredElemnts.push(item)
        } else if (item?.horizontalComps) {
          const horFilteredItems: any = []
          item?.horizontalComps?.filter((horItem: any) => {
            if (horItem?.dependency || horItem?.hideComponent) {
              let horFlag = true
              if (horItem?.dependency) {
                for (let i = 0; i < horItem.dependency.length - 1; i += 2) {
                  if (
                    typeof horItem.dependency[i + 1] === 'string' &&
                    propsFromFormik.values[horItem.dependency[i]] !== horItem.dependency[i + 1]
                  ) {
                    horFlag = false
                    break
                  }
                  if (typeof horItem.dependency[i + 1] !== 'string') {
                    for (let j = 0; j < horItem.dependency[i + 1].length; j++) {
                      if (propsFromFormik.values[horItem.dependency[i]] === horItem.dependency[i + 1][j]) {
                        horFlag = true
                        break
                      } else {
                        horFlag = false
                      }
                    }
                  }
                }
              }
              if (horFlag && horItem?.hideComponent) {
                horFlag = !horItem?.hideComponent(propsFromFormik.values)
              }
              horFlag && horFilteredItems.push(horItem)
            } else {
              horFilteredItems.push(horItem)
            }
          })
          horFilteredItems.length > 0 && filteredElemnts.push({ horizontalComps: [...horFilteredItems] })
        } else {
          filteredElemnts.push(item)
        }
      })
      return filteredElemnts
    }
    const shouldShowAddMore =
      !!formData[step].addMore &&
      (!formData[step].shouldShowAddMore || formData[step].shouldShowAddMore!(propsFromFormik.values))

    const changeStep = (key: string) => {
      const nextStep = formData.findIndex((item) => item.key === key)
      if (nextStep === -1) {
        throw `No such step ${key}`
      }
      if (!formData[step].noBackStack && !backStack.current.includes(step)) {
        backStack.current.push(step)
      }
      setStep(nextStep)
    }

    const validateValues = async () => {
      const allStepValidation = await Promise.all(schemaArray.map((item) => item.isValid(propsFromFormik.values)))
      const isValid = allStepValidation[step]
      stepValidationStatus.current = allStepValidation
      if (isValid !== validForm) {
        setValidForm(isValid)
      }
    }
    useEffect(() => {
      validateValues()
    })
    const currentCount = addmoreCount?.[step.toString()] ?? 0

    const addedRowExist = formData[step]?.formItems?.some(
      (item) =>
        item.fieldName === `${formData[step]?.addMore?.[0]}_${currentCount}` ||
        item.fieldName === `${formData[step]?.addMore?.[0]}`,
    )
    return (
      <Container>
        <FormGenerator
          formData={getFilteredItems()}
          handleChange={propsFromFormik.handleChange}
          setFieldValue={propsFromFormik.setFieldValue}
          errors={propsFromFormik.errors}
          touched={propsFromFormik.touched}
          values={propsFromFormik.values}
          isValid={propsFromFormik.isValid}
          setFieldTouched={propsFromFormik.setFieldTouched}
          isSubmitting={propsFromFormik.isSubmitting}
          onFormSubmit={onFormSubmit}
          reloadStep={reloadStep}
          changeStep={changeStep}
          allTouched={allTouched}
          verticalSpacing={formData[step].verticalSpacing}
          horizontalFlex={formData[step].horizontalFlex}
          verticalFlex={formData[step].verticalFlex}
          contentOverflow={formData[step].contentOverflow}
          contentPadding={formData[step].contentPadding}
        />
        {shouldShowAddMore && (
          <>
            <Box onPress={addMoreClicked} underlayColor="TRANSPARENT">
              {formData[step].customAddMoreView ?? (
                <Box {...styles.addMoreContainer}>
                  <Text type="h4" color="DARK_GREY">
                    + Add more
                  </Text>
                </Box>
              )}
            </Box>
            {addmoreCount?.[step] > 0 && addedRowExist && (
              <Box {...styles.addMoreContainer} onPress={deleteRowClicked} underlayColor="TRANSPARENT">
                <Text type="h4" color="DARK_GREY">
                  Remove Last Added Row
                </Text>
              </Box>
            )}
          </>
        )}
        {actionButtons({
          handleSubmit: propsFromFormik.handleSubmit,
          isValid: propsFromFormik.isValid,
          isCurrentFormValid: validForm,
          values: propsFromFormik.values,
          setSubmitting: propsFromFormik.setSubmitting,
          isSubmitting: propsFromFormik.isSubmitting,
          defaultActionButtonOnSubmit,
          title: formData[step].actionTitle,
          addedRowsCount: addmoreCount[step],
          resetForm: propsFromFormik.resetForm,
          allStepValidationStatus: stepValidationStatus.current,
        })}
      </Container>
    )
  },
)

export const FormContainer = ({ children }) => (
  <Box f={1} p={16}>
    {children}
  </Box>
)

const defaultActionButtons = ({ defaultActionButtonOnSubmit, title, isValid }: ActionButtonProps) => {
  return (
    <Button
      type="primary"
      isFull
      title={title}
      disabled={!isValid}
      onPress={() => {
        defaultActionButtonOnSubmit()
      }}
    />
  )
}

const Form = forwardRef(
  (
    {
      formData,
      onClose,
      actionButtons = defaultActionButtons,
      container = FormContainer,
      initialValues = {},
      initialStep = '',
      onFormSubmit,
      setTitle,
      isLoading,
      setError = () => {},
      onAddMoreClicked,
      allTouched = false,
    }: FormProps,
    ref: React.Ref<RefCalback>,
  ) => {
    const formRef = useRef<RefCalback>(null)
    let retData = {}
    const formSchema = Yup.object().shape(
      formData.reduce((acc, curArr) => {
        return curArr.formItems
          .filter((item) => item.hide !== true)
          .reduce((accItem, curItem) => {
            if (curItem.validationSchema) {
              return {
                ...accItem,
                [curItem.fieldName]: curItem.validationSchema,
              }
            } else {
              curItem?.horizontalComps?.map((curHorItem: any) => {
                if (curHorItem.validationSchema) {
                  retData = {
                    ...retData,
                    [curHorItem.fieldName]: curHorItem.validationSchema,
                  }
                }
                return retData
              })
              return {
                ...retData,
                ...accItem,
              }
            }
          }, acc)
      }, {}),
    )
    useImperativeHandle(ref, () => ({
      goBack: () => {
        if (formRef && formRef.current !== null) {
          return formRef.current.goBack()
        }
        return false
      },
      moveToNextStep: () => {
        if (formRef && formRef.current !== null) {
          return formRef.current.moveToNextStep()
        }
        return false
      },
      getValues: () => {
        if (formRef && formRef.current !== null) {
          return formRef.current.getValues()
        }
        return {}
      },
      setFieldValue: (key, value) => {
        if (formRef && formRef.current !== null) {
          return formRef.current.setFieldValue(key, value)
        }
        return {}
      },
    }))

    const onSubmit = () => {
      if (onClose) {
        onClose()
      }
    }

    return (
      <Formik
        initialValues={initialValues}
        initialStep={initialStep}
        enableReinitialize
        validationSchema={formSchema}
        onSubmit={onSubmit}
        validateOnMount
        validateOnBlur>
        {(propsFromFormik) => (
          <RenderForm
            {...propsFromFormik}
            initialStep={initialStep}
            actionButtons={actionButtons}
            formData={formData}
            onFormSubmit={onFormSubmit}
            ref={formRef}
            setTitle={setTitle}
            isLoading={isLoading}
            setError={setError}
            container={container}
            onAddMoreClicked={onAddMoreClicked}
            allTouched={allTouched}
          />
        )}
      </Formik>
    )
  },
)

export type { ActionButtonProps, FormPageItem } from './types'
export { Form }
export type { FormTypes }
